import {computed, onBeforeUnmount, ref, watch, type Ref} from 'vue';
import type Hls from 'hls.js';

/** Global audio singleton — only one media plays at a time */
let activeMediaEl: HTMLMediaElement | null = null;

/** Options for the useMediaPlayer composable */
interface UseMediaPlayerOptions {
  hlsSrc?: string;
}

/** Return type of the useMediaPlayer composable */
interface UseMediaPlayerReturn {
  isPlaying: Ref<boolean>;
  hasBeenStarted: Ref<boolean>;
  canPlay: Ref<boolean>;
  currentTime: Ref<number>;
  /** Seconds of the media actually played, seeks excluded. See {@link playedSeconds}. */
  played: Ref<number>;
  duration: Ref<number>;
  isMuted: Ref<boolean>;
  playbackRate: Ref<number>;
  formattedCurrentTime: Ref<string>;
  formattedDuration: Ref<string>;
  play(request?: {withoutGesture?: boolean}): Promise<void>;
  pause(): void;
  seekTo(time: number): void;
  toggleMute(): void;
  setPlaybackRate(rate: number): void;
  requestFullscreen(): void;
  initHls(): Promise<void>;
  startHlsLoad(): void;
  destroy(): void;
}

function formatTime(seconds: number): string {
  const totalSec = Math.round(seconds);
  const min = Math.floor(totalSec / 60);
  const sec = String(totalSec % 60).padStart(2, '0');
  return `${min}:${sec}`;
}

/**
 * How much of the media has actually been played, from the element's own record of it.
 *
 * Read rather than counted: the progress bar and the skip buttons move `currentTime` themselves
 * (QSlider reports every pointer move of a drag), so anything inferred from that would take a drag
 * across the bar for a viewing. `played` only grows while frames are shown, and a seek adds a new
 * range instead of time, so watching half of something in two sittings still adds up to half.
 */
function playedSeconds(mediaEl: HTMLMediaElement | undefined): number {
  const played = mediaEl?.played;
  let total = 0;
  for (let range = 0; range < (played?.length ?? 0); range++) {
    total += (played?.end(range) ?? 0) - (played?.start(range) ?? 0);
  }

  return total;
}

function createMediaListeners(
  mediaEl: Ref<HTMLMediaElement | undefined>,
  state: {
    currentTime: Ref<number>;
    played: Ref<number>;
    duration: Ref<number>;
    isPlaying: Ref<boolean>;
    hasBeenStarted: Ref<boolean>;
    canPlay: Ref<boolean>;
    isMuted: Ref<boolean>;
    volume: Ref<number>;
  },
): {cleanup(): void} {
  function onTimeUpdate(): void {
    state.currentTime.value = mediaEl.value?.currentTime ?? 0;
    state.played.value = playedSeconds(mediaEl.value);
  }

  function onDurationChange(): void {
    state.duration.value = mediaEl.value?.duration ?? 0;
  }

  function onPlaying(): void {
    state.isPlaying.value = true;
    state.hasBeenStarted.value = true;
  }

  function onPause(): void {
    state.isPlaying.value = false;
  }

  function onEnded(): void {
    state.isPlaying.value = false;
  }

  function onCanPlay(): void {
    state.canPlay.value = true;
  }

  function onVolumeChange(): void {
    state.isMuted.value = mediaEl.value?.muted ?? false;
    state.volume.value = mediaEl.value?.volume ?? 1;
  }

  const events = {
    timeupdate: onTimeUpdate,
    durationchange: onDurationChange,
    playing: onPlaying,
    pause: onPause,
    ended: onEnded,
    canplay: onCanPlay,
    volumechange: onVolumeChange,
  } as const;

  function attach(el: HTMLMediaElement): void {
    for (const [event, handler] of Object.entries(events)) {
      el.addEventListener(event, handler);
    }
  }

  function detach(el: HTMLMediaElement): void {
    for (const [event, handler] of Object.entries(events)) {
      el.removeEventListener(event, handler);
    }
  }

  const stopWatch = watch(mediaEl, (newEl, oldEl) => {
    if (oldEl) {
      detach(oldEl);
    }
    if (newEl) {
      attach(newEl);
    }
  }, {immediate: true});

  return {
    cleanup(): void {
      stopWatch();
      if (mediaEl.value) {
        detach(mediaEl.value);
      }
    },
  };
}

/** Composable for managing HTML media element playback state */
function useMediaPlayer(
  mediaEl: Ref<HTMLMediaElement | undefined>,
  options?: UseMediaPlayerOptions,
): UseMediaPlayerReturn {
  const canPlay = ref(false), hasBeenStarted = ref(false), isPlaying = ref(false);
  const currentTime = ref(0), duration = ref(0), played = ref(0);
  const isMuted = ref(false), playbackRate = ref(1), volume = ref(1);
  const formattedCurrentTime = computed(() => formatTime(currentTime.value));
  const formattedDuration = computed(() => formatTime(duration.value));

  let hls: Hls | null = null;

  const {cleanup: cleanupListeners} = createMediaListeners(mediaEl, {
    currentTime, played, duration, isPlaying, hasBeenStarted, canPlay, isMuted, volume,
  });

  /**
   * Starts this element, taking the one-media-at-a-time singleton from whatever held it. What was
   * playing is left where it was rather than sent back to the start: since IPD-2980 a player lives
   * in the FAQ panel, which opens over any page, and rewinding would throw away someone's place in
   * the session media underneath it — a place the app cannot restore and the reader cannot know.
   *
   * `withoutGesture` for a play nobody asked for (the welcome video, IPD-3105). Such a play can be
   * refused — every browser blocks one that would make noise, and a phone saving power refuses even
   * a muted one — and taking the singleton before knowing stopped the reader's playback for a
   * playback that never began, leaving nothing playing at all and the global pointing at an element
   * that is not playing, so the next real play paused the wrong thing. So it waits until this
   * element is running, and the two overlap until then.
   *
   * A play behind a gesture keeps the eager hand-over: it is not going to be refused, and waiting
   * would let the reader hear both at once for as long as this element takes to buffer.
   */
  /**
   * Claims the one-media-at-a-time singleton, pausing whoever held it. What was playing is left
   * where it was rather than sent back to the start: since IPD-2980 a player lives in the FAQ
   * panel, which opens over any page, and rewinding would throw away someone's place in the session
   * media underneath it — a place the app cannot restore and the reader cannot know.
   *
   * Only for an element that is making sound. The rule is about the reader's ears, and a muted
   * playback is not competing for them: the welcome video plays itself muted (IPD-3105), and
   * silencing audio the reader chose, for a video nobody asked for and that says nothing, is a
   * straight loss with no explanation on screen. A muted playback claims the singleton the moment
   * it is unmuted — see `toggleMute`.
   */
  function takeOverActive(): void {
    if (!mediaEl.value || mediaEl.value.muted) {
      return;
    }
    if (activeMediaEl && activeMediaEl !== mediaEl.value) {
      activeMediaEl.pause();
    }
    activeMediaEl = mediaEl.value;
  }

  async function play(request?: {withoutGesture?: boolean}): Promise<void> {
    if (!mediaEl.value) {
      return;
    }
    if (!request?.withoutGesture) {
      takeOverActive();
    }
    await mediaEl.value.play();
    if (request?.withoutGesture) {
      takeOverActive();
    }
    hasBeenStarted.value = true;
  }

  function pause(): void {
    mediaEl.value?.pause();
  }

  function seekTo(time: number): void {
    if (mediaEl.value) {
      const clamped = Math.max(0, Math.min(time, duration.value));
      currentTime.value = clamped;
      mediaEl.value.currentTime = clamped;
    }
  }

  function toggleMute(): void {
    if (mediaEl.value) {
      isMuted.value = !isMuted.value;
      mediaEl.value.muted = isMuted.value;
      // Becoming audible is what claims the singleton: a muted playback left whatever the reader
      // was listening to alone, and must not now talk over it. See `takeOverActive`.
      takeOverActive();
    }
  }

  function setPlaybackRate(rate: number): void {
    if (mediaEl.value) {
      mediaEl.value.playbackRate = rate;
      playbackRate.value = rate;
    }
  }

  function requestFullscreen(): void {
    void mediaEl.value?.requestFullscreen?.();
  }

  async function initHls(): Promise<void> {
    if (!options?.hlsSrc || !mediaEl.value) {
      return;
    }

    const isHlsSource = options.hlsSrc.endsWith('.m3u8');
    if (!isHlsSource) {
      mediaEl.value.src = options.hlsSrc;
      canPlay.value = true;
      return;
    }

    const HlsModule = await import('hls.js');
    const HlsClass = HlsModule.default;

    if (HlsClass.isSupported()) {
      hls = new HlsClass({autoStartLoad: false});
      hls.loadSource(options.hlsSrc);
      hls.attachMedia(mediaEl.value);
      hls.once(HlsClass.Events.MANIFEST_PARSED, () => {
        canPlay.value = true;
      });
    } else if (mediaEl.value.canPlayType('application/vnd.apple.mpegurl')) {
      mediaEl.value.src = options.hlsSrc;
    }
  }

  function startHlsLoad(): void {
    hls?.startLoad();
  }

  function destroy(): void {
    cleanupListeners();
    if (mediaEl.value) {
      mediaEl.value.pause();
      if (activeMediaEl === mediaEl.value) {
        activeMediaEl = null;
      }
    }
    if (hls) {
      hls.destroy();
      hls = null;
    }
  }

  // Before rather than after: Vue clears the template ref as it unmounts, so from `onUnmounted` the
  // element is already null and the body above never runs — the media is never paused by the app
  // and the global keeps pointing at a player that no longer exists.
  onBeforeUnmount(destroy);

  return {
    isPlaying,
    hasBeenStarted,
    canPlay,
    currentTime,
    played,
    duration,
    isMuted,
    playbackRate,
    formattedCurrentTime,
    formattedDuration,
    play,
    pause,
    seekTo,
    toggleMute,
    setPlaybackRate,
    requestFullscreen,
    initHls,
    startHlsLoad,
    destroy,
  };
}

export {formatTime, useMediaPlayer};
export type {UseMediaPlayerOptions, UseMediaPlayerReturn};
